import { ChangeDetectionStrategy, Component, Directive, Input } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { BaseLayerManagerComponent } from "../base-layer-manager/base-layer-manager.component";
import { Viewerlayer } from "../../../model/viewerlayer";
import { OverlayDialogComponent } from "../../../../dialog-components/overlay-dialog/overlay-dialog.component";
import { LayerCommunicatorService } from "../../../services/communicators/layer-communicator.service";
import { ToolbarService } from "../../../services/internal-services/toolbar.service";
import { FeaturesSearchService } from "../../../services/internal-services/features-search.service";
import { LayerService } from "../../../services/internal-services/layer.service";
import { ILayerResponse } from "../../../interfaces/i-layer-response";
import { EditLayerDialogComponent } from "../../../../dialog-components/edit-layer-dialog/edit-layer-dialog.component";
import { IEditLayerRequest } from "../../../interfaces/i-edit-layer-request";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { ApiViewerService } from "../../../services/internal-services/api-viewer.service";
import { MapService } from "../../../services/internal-services/map.service";
import { FeatureFlagsService } from "../../../services/internal-services/feature-flags.service";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { AddUserDataDialogComponent } from "../../../../dialog-components/add-user-data-dialog/add-user-data-dialog.component";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-overlay-manager",
  templateUrl: "./overlay-manager.component.html",
  styleUrls: ["./overlay-manager.component.scss"],
  standalone: false,
})
export class OverlayManagerComponent extends BaseLayerManagerComponent {
  _overlayLayers: Viewerlayer[];

  @Input() set overlayLayers(layers: Viewerlayer[]) {
    this._overlayLayers = layers;
  }

  get overlayLayers(): Viewerlayer[] {
    return this._overlayLayers;
  }

  showUploadDialog() {
    const dialog = this.matDialog.open(AddUserDataDialogComponent, {
      data: {
        title: "Add WMS or WMTS layer from URL",
        viewerService: this.viewerService,
      },
      panelClass: "medium-panel-dialog",
    });
    dialog.afterClosed().subscribe(() => {});
  }

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected mapService: MapService,
    protected layerCommunicatorService: LayerCommunicatorService,
    protected matDialog: MatDialog,
    protected toolbarService: ToolbarService,
    protected featuresServiceSearch: FeaturesSearchService,
    protected layerService: LayerService,
    protected apiViewerService: ApiViewerService,
    protected flagsService: FeatureFlagsService,
  ) {
    super(
      googleAnalyticsService,
      mapService,
      layerCommunicatorService,
      matDialog,
      toolbarService,
      featuresServiceSearch,
      layerService,
      flagsService,
    );
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.subscriptions.push(
      this.layerCommunicatorService.editLayerResponse$.subscribe((response: ILayerResponse) => {
        if (response && response.status === 200) {
          this.viewerService.updateOverlayLayers(this.overlayLayers);
          this.viewerService.updateOverlayStyles();
        }
      }),
    );
  }

  removeUserLayer(layerId: string) {
    const dialog = this.matDialog.open(OverlayDialogComponent, {
      data: {
        text: "Are you sure you want to remove this layer?",
        isConfirmation: true,
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.viewerService.removeUserLayer(layerId);
      }
    });
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_REMOVE,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  changeOverlayOrder(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.overlayLayers, event.previousIndex, event.currentIndex);
    this.viewerService.updateOverlayOrder(this.overlayLayers);
  }

  changeVisibility(event: any) {
    const { layer } = event;

    if (layer.owner) {
      this.viewerService.changeUserLayerVisibility(event.layer.id, event.visibility);
      this.viewerService.updateUserLayer(event.layer);
    } else {
      this.viewerService.changeLayerVisibility(event.layer.id, event.visibility, false);

      if (layer?.layerType === ViewerConstants.LAYER_TYPE.WMSLAYER && layer.layersAvailable) {
        layer.layersAvailable.map((layerAvailable) => (layerAvailable.visible = event.visibility));
        this.changeWMSChildVisibility(null, null, layer);
      } else {
        layer.childs?.map((child) => this.viewerService.changeLayerVisibility(child.id, event.visibility, false));
      }
    }
    this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
  }

  changeChildVisibility(event: any) {
    const { layer } = event;
    const { child } = event;
    const { visibility } = event;

    if (child) child.visible = visibility;
    // Update parent layer object status
    layer.visible = !!layer.layersAvailable?.find((l) => l.visible) || !!layer.childs?.find((l) => l.visible);
    // Update layers on map
    if (layer?.layerType === ViewerConstants.LAYER_TYPE.WMSLAYER) {
      this.viewerService.getCoreMap().changeVisibilityWMSChild(layer.id);
    } else {
      this.viewerService.changeLayerVisibility(child.id, visibility, false);
    }
    this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
  }

  editLayer(layerId) {
    const overlayLayer = this.overlayLayers.find((layer) => layerId === layer.id);
    const dialog = this.matDialog.open(EditLayerDialogComponent, {
      data: {
        layer: overlayLayer,
        usedColors: this.usedColors,
      },
      autoFocus: false,
    });
    dialog.afterClosed().subscribe((result) => {
      if (result && result.status === 200) {
        overlayLayer.style = JSON.stringify(result.style);
        overlayLayer.opacity = result.opacity;
        this.viewerService.updateOverlayLayers(this.overlayLayers);

        const editRequest: IEditLayerRequest = {
          wfsLayerStyle: result.style,
          instanceId: this.viewerService.getInstanceId(),
          layerId,
        };

        this.layerCommunicatorService.editLayerRequest(editRequest);
      }
    });
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MY_DATA_EDIT_STYLE,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  hideOthers(layerId: string) {
    super.hideOthers(layerId);
    this.overlayLayers
      .filter((layer) => layer.id !== layerId)
      .map((layer) => {
        layer.visible = false;
        this.viewerService.changeLayerVisibility(layer.id, false, false);
        return layer;
      });
  }
}
